import type { Variant } from '../../gameTypes.js';
import { hashStringToSeed, mulberry32 } from '../../seededRng.js';

/**
 * Seeded RNG streams for the jump pattern composer. The primitives live
 * in `../../seededRng.js` (shared with the other procedural generators);
 * this module just wires up the named streams a chart is derived from.
 */

/**
 * Four independent RNG streams the composer draws from. Keeping them separate
 * means tweaking one decision's logic (which pattern, which twist, what
 * geometry, where pits go) doesn't ripple the others' rolls — so a change to
 * twist selection can't shift every pattern's geometry. Each `() => number`
 * returns a fresh value in [0, 1).
 */
export interface JumpPatternRng {
  /** Picks which pattern fills the next run of onsets. */
  select: () => number;
  /** Picks whether/which twist a chosen pattern applies. */
  twist: () => number;
  /** Picks geometry within a pattern (heights, widths, which tread is spiked). */
  place: () => number;
  /** Environmental hazards layered after the patterns (pits in long breaks). */
  feature: () => number;
}

/** Build the seeded streams for a `(slug, variant)` pair. */
export function createJumpPatternRng(slug: string, variant: Variant): JumpPatternRng {
  return {
    select: mulberry32(hashStringToSeed(`${slug}:${variant}:jump:pattern:select`)),
    twist: mulberry32(hashStringToSeed(`${slug}:${variant}:jump:pattern:twist`)),
    place: mulberry32(hashStringToSeed(`${slug}:${variant}:jump:pattern:place`)),
    feature: mulberry32(hashStringToSeed(`${slug}:${variant}:jump:pattern:feature`)),
  };
}
